import { Box, CircularProgress, Paper } from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColumns,
  GridRenderCellParams,
  GridRowParams,
} from "@mui/x-data-grid";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { formatDate } from "@/lib/time";

import { CreateGameDialog } from "@/modules/administration/Games/CreateGameDialog";
import { Button } from "@/modules/common/components/Button";
import { CopyToClipboard } from "@/modules/common/components/CopyToClipboard";
import { Dialog } from "@/modules/common/components/Dialog";
import { Icon } from "@/modules/common/components/Icon";
import { Tag } from "@/modules/common/components/Tag";
import { Typography } from "@/modules/common/components/Typography";
import { useDialog } from "@/modules/common/hooks/useDialog";
import { I18nTranslateFunction } from "@/modules/translations";
import { useTranslation } from "@/modules/translations/useTranslation";
import { http } from "@/utils/request";
import { IGameWithTeacher } from "@/utils/types";

import { useRemoveGameMutation } from "./Game/services/mutations";

export { Games };

function Games(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const handleGameCreated = () => {
    queryClient.invalidateQueries(["games"]);
  };

  return (
    <>
      <Box alignItems="center" display="flex" justifyContent="space-between">
        <Typography variant="h3">{t("page.admin.games.title")}</Typography>
        <CreateGameDialog onGameCreated={handleGameCreated} />
      </Box>
      <Paper sx={{ mt: 2, p: 2 }}>
        <GamesDataGrid />
      </Paper>
    </>
  );
}

const buildColumns = ({
  idOfGameBeingRemoved,
  removeGame,
  t,
}: {
  idOfGameBeingRemoved?: number;
  removeGame: (gameId: number) => void;
  t: I18nTranslateFunction;
}): GridColumns<IGameWithTeacher> => [
  { field: "id", headerName: t("table.column.game-id.label"), width: 80 },
  {
    field: "code",
    headerName: t("table.column.game-code.label"),
    width: 120,
    renderCell: (params: GridRenderCellParams<string>) => {
      return (
        <CopyToClipboard className="h-full w-full" value={params.value || ""} />
      );
    },
  },
  {
    field: "name",
    headerName: t("table.column.game-name.label"),
    flex: 1,
    minWidth: 150,
    renderCell: (params: GridRenderCellParams<string>) => {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          {params.row.isTest && (
            <Tag type="secondary">{t("game.mode.test").toUpperCase()}</Tag>
          )}
          {params.value}
        </Box>
      );
    },
  },
  {
    field: "teacher",
    headerName: t("table.column.game-teacher.label"),
    width: 250,
    valueGetter: (params) => params.row.teacher.email,
    flex: 1,
    minWidth: 150,
  },
  {
    field: "date",
    headerName: t("table.column.game-date.label"),
    type: "dateTime",
    renderCell: (params: GridRenderCellParams<string>) => {
      return formatDate(new Date(params.row.date), "medium-date-at-time");
    },
    flex: 1,
    minWidth: 150,
  },
  {
    field: "status",
    headerName: t("table.column.game-status.label"),
    valueGetter: (params) => t(`game.status.${params.row.status}`),
    flex: 1,
    minWidth: 150,
  },
  {
    field: "actions",
    type: "actions",
    headerName: t("table.column.actions.label"),
    width: 80,
    getActions: (params: GridRowParams<IGameWithTeacher>) => [
      <GridActionsCellItem
        icon={<Icon name="delete" />}
        label={`Delete game ${params.row.name}`}
        onClick={() => removeGame(params.row.id)}
        disabled={idOfGameBeingRemoved === params.row.id}
        // Quick typing fix. See mui.d.ts for more details.
        {...({} as any)}
      />,
    ],
  },
];

function GamesDataGrid() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [idOfGameToRemove, setIdOfGameToRemove] = useState<number | null>(null);
  const {
    isOpen: isRemoveGameDialogOpen,
    openDialog: openRemoveGameDialog,
    closeDialog: closeRemoveGameDialog,
  } = useDialog();

  const query = useQuery(["games"], () => {
    return http.get<undefined, { data: { documents: any[] } }>("/api/games");
  });
  const { removeGameMutation } = useRemoveGameMutation();

  const onRemoveGame = useCallback(
    (gameId: number) => {
      setIdOfGameToRemove(gameId);
      openRemoveGameDialog();
    },
    [openRemoveGameDialog, setIdOfGameToRemove],
  );

  const removeGame = useCallback(() => {
    if (idOfGameToRemove != null) {
      removeGameMutation.mutate({ gameId: idOfGameToRemove });
    }
  }, [idOfGameToRemove, removeGameMutation]);

  if (query.isLoading) {
    return <CircularProgress />;
  }

  const rows: IGameWithTeacher[] = query?.data?.data?.documents ?? [];

  return (
    <>
      <Box style={{ height: 600, width: "100%", cursor: "pointer" }}>
        <DataGrid
          rows={rows}
          columns={buildColumns({
            idOfGameBeingRemoved: removeGameMutation.variables?.gameId,
            removeGame: onRemoveGame,
            t,
          })}
          disableSelectionOnClick
          initialState={{
            sorting: {
              sortModel: [{ field: "date", sort: "desc" }],
            },
          }}
          pageSize={20}
          rowsPerPageOptions={[20]}
          onRowClick={(rowData) => {
            const path = rowData.id
              ? `/administration/games/${rowData.id}`
              : "/administration/games/";
            return navigate(path);
          }}
        />
      </Box>
      <Dialog
        open={isRemoveGameDialogOpen}
        content={
          <Typography>
            {t("modal.remove-game.title", { game: idOfGameToRemove })}
          </Typography>
        }
        actions={
          <>
            <Button type="secondary" onClick={closeRemoveGameDialog}>
              {t("cta.cancel")}
            </Button>
            <Button
              onClick={() => {
                removeGame();
                closeRemoveGameDialog();
              }}
            >
              {t("cta.delete")}
            </Button>
          </>
        }
        handleClose={closeRemoveGameDialog}
      />
    </>
  );
}
